/**
* Copyright (c) 2025 Bytedance, Inc. and its affiliates.
* SPDX-License-Identifier: Apache-2.0
*/
var types_LogLevel = /*#__PURE__*/ function(LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["SUCCESS"] = 2] = "SUCCESS";
    LogLevel[LogLevel["WARN"] = 3] = "WARN";
    LogLevel[LogLevel["ERROR"] = 4] = "ERROR";
    LogLevel[LogLevel["SILENT"] = 5] = "SILENT";
    return LogLevel;
}({});
class BaseLogger {
    log(...args) {}
    info(...args) {}
    warn(...args) {}
    error(...args) {}
    debug(...args) {}
    success(message) {}
    infoWithData(message, data, transformer) {}
    spawn(subPrefix) {
        return new BaseLogger();
    }
    setLevel(level) {}
    getLevel() {
        return 1;
    }
}
const defaultLogger = new BaseLogger();
var _process_stdout;
const colorize_isBrowser = 'undefined' != typeof window && void 0 !== window.document;
const supportsColor = !colorize_isBrowser && !('NO_COLOR' in process.env || '0' === process.env.FORCE_COLOR) && (void 0 !== process.env.FORCE_COLOR || (null === (_process_stdout = process.stdout) || void 0 === _process_stdout ? void 0 : _process_stdout.isTTY));
const ANSI_COLORS = {
    black: '\x1b[30m',
    red: '\x1b[31m',
    green: '\x1b[32m',
    yellow: '\x1b[33m',
    blue: '\x1b[34m',
    magenta: '\x1b[35m',
    cyan: '\x1b[36m',
    white: '\x1b[37m',
    gray: '\x1b[90m',
    reset: '\x1b[0m'
};
const ANSI_STYLES = {
    bold: '\x1b[1m',
    normal: ''
};
const CSS_COLOR_VALUES = {
    black: '#1E1E2E',
    red: '#F87171',
    green: '#10B981',
    yellow: '#FBBF24',
    blue: '#3B82F6',
    magenta: '#A78BFA',
    cyan: '#06B6D4',
    white: '#F3F4F6',
    gray: '#9CA3AF',
    reset: 'inherit'
};
const CSS_COLORS = {};
Object.entries(CSS_COLOR_VALUES).forEach(([key, value])=>{
    CSS_COLORS[key] = `color: ${value}`;
});
const CSS_STYLES = {
    bold: 'font-weight: bold',
    normal: ''
};
function colorize(text, color, style = 'normal') {
    if (!text) return text;
    if (colorize_isBrowser) return text;
    if (supportsColor) {
        let result = text;
        if ('normal' !== style) result = `${ANSI_STYLES[style]}${result}`;
        result = `${ANSI_COLORS[color]}${result}`;
        return `${result}${ANSI_COLORS.reset}`;
    }
    return text;
}
function colorLog(text, color, style = 'normal') {
    if (colorize_isBrowser) {
        let cssStyle = CSS_COLORS[color] || '';
        if ('normal' !== style) cssStyle += '; ' + CSS_STYLES[style];
        console.log(`%c${text}`, cssStyle);
    } else if (supportsColor) console.log(colorize(text, color, style));
    else console.log(text);
}
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
class ConsoleLogger extends BaseLogger {
    colorPrefix(prefix, type) {
        if (!prefix) return '';
        let color = 'gray';
        switch(type){
            case 'info':
                color = 'blue';
                break;
            case 'warn':
                color = 'yellow';
                break;
            case 'error':
                color = 'red';
                break;
            case 'debug':
                color = 'gray';
                break;
            case 'success':
                color = 'green';
                break;
        }
        const isBrowser = 'undefined' != typeof window && void 0 !== window.document;
        if (isBrowser) {
            this.lastPrefixColor = color;
            return prefix;
        }
        return colorize(prefix, color, 'bold');
    }
    log(...args) {
        if (this.level <= types_LogLevel.DEBUG) console.log(this.colorPrefix(this.prefix), ...args);
    }
    info(...args) {
        if (this.level <= types_LogLevel.INFO) {
            const prefix = this.colorPrefix(this.prefix, 'info');
            if ('undefined' != typeof window && this.lastPrefixColor) {
                console.log(`%c${prefix}%c`, `color: ${CSS_COLOR_VALUES[this.lastPrefixColor]}; font-weight: bold`, 'color: inherit', ...args);
                this.lastPrefixColor = null;
            } else console.log(`${prefix}`, ...args);
        }
    }
    warn(...args) {
        if (this.level <= types_LogLevel.WARN) {
            const prefix = this.colorPrefix(this.prefix, 'warn');
            if ('undefined' != typeof window && this.lastPrefixColor) {
                console.warn(`%c${prefix}%c`, `color: ${CSS_COLOR_VALUES[this.lastPrefixColor]}; font-weight: bold`, 'color: inherit', ...args);
                this.lastPrefixColor = null;
            } else console.warn(`${prefix}`, ...args);
        }
    }
    error(...args) {
        if (this.level <= types_LogLevel.ERROR) {
            const prefix = this.colorPrefix(this.prefix, 'error');
            if ('undefined' != typeof window && this.lastPrefixColor) {
                console.error(`%c${prefix}%c`, `color: ${CSS_COLOR_VALUES[this.lastPrefixColor]}; font-weight: bold`, 'color: inherit', ...args);
                this.lastPrefixColor = null;
            } else console.error(`${prefix}`, ...args);
        }
    }
    success(message) {
        if (this.level <= types_LogLevel.SUCCESS) {
            const prefix = this.colorPrefix(this.prefix, 'success');
            if ('undefined' != typeof window && this.lastPrefixColor) {
                console.log(`%c${prefix}%c ${message}`, `color: ${CSS_COLOR_VALUES[this.lastPrefixColor]}; font-weight: bold`, 'color: inherit');
                this.lastPrefixColor = null;
            } else console.log(`${prefix} ${message}`);
        }
    }
    debug(...args) {
        if (this.level <= types_LogLevel.DEBUG) {
            const prefix = this.colorPrefix(this.prefix, 'debug');
            if ('undefined' != typeof window && this.lastPrefixColor) {
                console.debug(`%c${prefix}%c`, `color: ${CSS_COLOR_VALUES[this.lastPrefixColor]}; font-weight: bold`, 'color: inherit', ...args);
                this.lastPrefixColor = null;
            } else console.debug(`${prefix}`, ...args);
        }
    }
    infoWithData(message, data, transformer) {
        if (this.level <= types_LogLevel.INFO) {
            this.info(message);
            if (data) console.log(transformer ? transformer(data) : data);
        }
    }
    spawn(prefix) {
        const newPrefix = this.prefix ? `${this.prefix}:${prefix}` : prefix;
        return new ConsoleLogger(newPrefix, this.level);
    }
    setLevel(level) {
        this.level = level;
    }
    getLevel() {
        return this.level;
    }
    constructor(prefix = '', level = types_LogLevel.INFO){
        super(), _define_property(this, "prefix", void 0), _define_property(this, "lastPrefixColor", null), _define_property(this, "level", types_LogLevel.INFO);
        this.prefix = prefix;
        this.level = level;
    }
}
export { BaseLogger, ConsoleLogger, types_LogLevel as LogLevel, colorLog, colorize, defaultLogger };

//# sourceMappingURL=index.mjs.map